"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, useEffect } from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import { navbarItems, NavItemLan } from "@/constants";
import { usePathname } from "next/navigation";
import LogoNav from "@/assets/Images/Logo.png";
import ArrowDown from "@/assets/Icons/arrowDown";
import LoginIcon from "@/assets/Icons/loginIcon";

export default function Navbar() {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Properly type the parameter
  const isItemActive = (item: NavItemLan): boolean => {
    if (item.url) {
      return pathname === item.url || pathname.startsWith(`${item.url}/`);
    }

    if (item.children) {
      return item.children.some((child: NavItemLan) => isItemActive(child));
    }

    return false;
  };

  // mobile
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const toggleDropdown = (key: string) => {
    setOpenDropdown((prevKey) => (prevKey === key ? null : key));
  };

  const isItemActiveMobile = (item: any) => {
    // Ganti dengan logika pengecekan active
    return false;
  };
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 9999; // Jumlah data per halaman

  return (
    <nav
      className={`bg-[#FBFFF6] top-0 py-5 flex justify-between items-center fixed w-full z-[999] transition-all duration-300 ${
        isScrolled ? "shadow-md py-4" : ""
      }`}
    >
      <div
        className={`container mx-auto flex justify-between items-center w-full`}
      >
        {/* Logo dan tombol menu mobile */}
        <div className="flex items-center justify-between w-full md:w-auto">
          <div className="flex gap-2 items-center">
            <Image
              src={LogoNav}
              alt="Logo"
              width={300}
              height={300}
              className="h-11 w-auto"
            />
            <div className="text-[#473D3D]">
              <div className="font-medium text-[12px]">Dinas Pertanian</div>
              <div className="text-[10px]">Kab. Penukal Abab Lematang Ilir</div>
            </div>
          </div>

          <button
            className="md:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none transition-transform hover:scale-110 active:scale-90 border"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Navigasi desktop */}
        <ul className="hidden md:flex space-x-3 items-center relative  text-sm">
          {navbarItems.map((item) => {
            const isActive = isItemActive(item);
            const hasChildren = item.children && item.children.length > 0;

            return (
              <li key={item.key} className="relative group">
                {item.url && !hasChildren ? (
                  <Link
                    href={item.url}
                    className={` transition-colors rounded-full p-1.5 px-2 ${
                      isActive
                        ? "font-semibold text-primary"
                        : " hover:text-primary"
                    }`}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <div
                    className={`cursor-pointer rounded-full p-1.5 px-2 transition-colors ${
                      isActive
                        ? "font-semibold text-primary"
                        : " hover:text-primary"
                    }`}
                  >
                    <div className="flex gap-1 items-center">
                      {item.label} <ArrowDown />
                    </div>
                  </div>
                )}

                {hasChildren && (
                  <ul className="absolute top-full left-0 mt-2 w-[180px] bg-white border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    {item.children?.map((child) => {
                      const isChildActive = isItemActive(child);
                      return (
                        <li key={child.key}>
                          <Link
                            href={child.url!}
                            className={`block px-4 py-2 text-sm hover:bg-gray-100 hover:text-primary ${
                              isChildActive
                                ? "font-bold text-primary "
                                : "text-[#473D3D]"
                            }`}
                          >
                            {child.label}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>

        {/* Tombol masuk */}
        <div>
          <Link
            href="/login"
            className="bg-primary hover:bg-primary-600 items-center gap-2 px-4 p-2.5 text-white hover:bg-primary-50 rounded-full hidden md:flex"
          >
            Masuk
            <LoginIcon />
          </Link>
        </div>
      </div>

      {/* Navigasi mobile */}
      <div
        className={`md:hidden absolute top-full left-0 right-0 bg-white shadow-lg transition-all duration-300 ease-in-out ${
          isOpen ? "max-h-screen py-4" : "max-h-0 py-0 overflow-hidden"
        }`}
      >
        <ul className="px-6 flex flex-col space-y-2">
          {navbarItems.map((item) => {
            const isActive = isItemActive(item);
            const isDropdownOpen = openDropdown === item.key;

            return (
              <li key={item.key} className="flex flex-col">
                {item.url && !item.children ? (
                  <Link
                    href={item.url}
                    className={`block py-2 hover:text-primary transition-colors ${
                      isActive ? "font-bold text-primary" : "text-[#627C8A]"
                    }`}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <>
                    <button
                      onClick={() => toggleDropdown(item.key)}
                      className={`flex justify-between items-center py-2 w-full ${
                        isActive ? "font-bold text-primary" : "text-[#627C8A]"
                      }`}
                    >
                      {item.label}
                      <ChevronDown
                        size={18}
                        className={`transition-transform duration-300 ${
                          isDropdownOpen ? "rotate-180" : "rotate-0"
                        }`}
                      />
                    </button>

                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        isDropdownOpen
                          ? "max-h-40 opacity-100"
                          : "max-h-0 opacity-0"
                      }`}
                    >
                      <ul className="pl-4 space-y-1 mt-1">
                        {item.children?.map((child) => {
                          const isChildActive = isItemActive(child);
                          return (
                            <li key={child.key}>
                              <Link
                                href={child.url!}
                                className={`block py-1 text-sm hover:text-primary ${
                                  isChildActive
                                    ? "font-bold text-primary"
                                    : "text-gray-600"
                                }`}
                              >
                                {child.label}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </>
                )}
              </li>
            );
          })}
          <li className="w-full pt-2">
            <Link
              href="/login"
              className="w-full inline-block p-2 text-center bg-primary text-white hover:bg-primary-50 rounded-full"
            >
              Masuk
            </Link>
          </li>
        </ul>
      </div>
    </nav>
  );
}
